import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Flag, ShieldAlert, ShieldCheck, Trash2, Users } from 'lucide-react'
import { db } from '../../lib/db'
import { brandConfirm } from '../../contexts/DialogContext'
import {
  adminDeleteCommunityPost,
  CommunityIntroductionRequestRow,
  CommunityMemberRow,
  CommunityPostRow,
  CommunityReportRow,
  closeCommunityIntroduction,
  fetchAdminUserId,
  fetchAllCommunityMembersAdmin,
  fetchAllCommunityPostsAdmin,
  fetchAllCommunityReportsAdmin,
  fetchAllIntroductionRequestsAdmin,
  HELP_FIND_CONNECTION_NOTE,
  resolveCommunityReport,
  setCommunityMemberSuspended,
  setCommunityPostHidden,
} from '../../lib/communityData'
import GlassCard from '../../components/ui/GlassCard'
import GradientButton from '../../components/ui/GradientButton'
import PanelShell from '../../components/ui/PanelShell'

type AdminTab = 'reports' | 'posts' | 'members' | 'introductions'

const ADMIN_TABS: { key: AdminTab; label: string; icon: typeof Flag }[] = [
  { key: 'reports', label: 'Reports', icon: Flag },
  { key: 'posts', label: 'Posts', icon: Eye },
  { key: 'members', label: 'Members', icon: Users },
  { key: 'introductions', label: 'Introductions', icon: ShieldCheck },
]

const formatDate = (ms: number) => new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })

const statusLabel: Record<string, string> = {
  pending: 'Pending',
  accepted: 'Accepted — ready for you to coordinate',
  declined: 'Declined',
  closed: 'Closed',
}

const AdminCommunity: React.FC = () => {
  const navigate = useNavigate()
  const { user } = db.useAuth()
  const token = (user as any)?.refresh_token || ''

  const [tab, setTab] = useState<AdminTab>('reports')
  const [loading, setLoading] = useState(true)
  const [reports, setReports] = useState<CommunityReportRow[]>([])
  const [posts, setPosts] = useState<CommunityPostRow[]>([])
  const [members, setMembers] = useState<CommunityMemberRow[]>([])
  const [introductions, setIntroductions] = useState<CommunityIntroductionRequestRow[]>([])
  const [adminUserId, setAdminUserId] = useState<string | null>(null)
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null)

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [r, p, m, i, adminId] = await Promise.all([
        fetchAllCommunityReportsAdmin(),
        fetchAllCommunityPostsAdmin(),
        fetchAllCommunityMembersAdmin(),
        fetchAllIntroductionRequestsAdmin(),
        fetchAdminUserId(),
      ])
      setReports(r)
      setPosts(p)
      setMembers(m)
      setIntroductions(i)
      setAdminUserId(adminId)
    } catch (err) {
      console.error('Failed to load Community Center admin data:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  /** userId -> display name, for turning raw ids into readable names below. */
  const memberNameOf = (userId: string) => {
    if (adminUserId && userId === adminUserId) return 'Cassie'
    return members.find((m) => m.userId === userId)?.displayName || 'Member'
  }

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const handleResolveReport = async (reportId: string) => {
    await resolveCommunityReport(reportId)
    loadAll()
  }

  const handleTogglePostHidden = async (post: CommunityPostRow) => {
    await setCommunityPostHidden(post.id, !post.hiddenByAdmin)
    loadAll()
  }

  /**
   * Permanent delete — distinct from Hide/Unhide above, which only ever
   * toggles visibility and keeps the row (a post an author "removed" stays
   * visible to admin forever as a moderation record). This actually erases
   * the post, its comments, and their reactions/reports. Irreversible, so
   * it always confirms first.
   */
  const handleDeletePost = async (post: CommunityPostRow) => {
    const ok = await brandConfirm({
      title: 'Permanently delete this post?',
      message: `This removes "${post.authorDisplayName}"'s post and its comments for good — not just hide it. This can't be undone.`,
      variant: 'danger',
      confirmLabel: 'Delete permanently',
      cancelLabel: 'Cancel',
    })
    if (!ok) return
    setDeletingPostId(post.id)
    try {
      await adminDeleteCommunityPost(post.id)
      await loadAll()
    } catch (err) {
      console.error('Failed to permanently delete post:', err)
    } finally {
      setDeletingPostId(null)
    }
  }

  const handleToggleSuspend = async (member: CommunityMemberRow) => {
    await setCommunityMemberSuspended(member.id, !member.suspended)
    loadAll()
  }

  const handleCloseIntroduction = async (requestId: string, closedReason: string) => {
    await closeCommunityIntroduction(token, { requestId, closedReason })
    loadAll()
  }

  const unresolvedReports = reports.filter((r) => !r.resolved)
  const resolvedReports = reports.filter((r) => r.resolved)

  return (
    <PanelShell
      eyebrow="Admin"
      title="Community Center moderation"
      subtitle="Reports, posts, members, and Cassie-mediated introductions."
      actions={
        <GradientButton onClick={() => navigate('/admin/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
          Back to admin dashboard
        </GradientButton>
      }
    >
      <div className="flex flex-wrap gap-2 mb-6">
        {ADMIN_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === key ? 'bg-ink text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
            }`}
          >
            <Icon size={15} />
            {label}
            {key === 'reports' && unresolvedReports.length > 0 && (
              <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-1.5">{unresolvedReports.length}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
        </div>
      ) : tab === 'reports' ? (
        <GlassCard hover={false}>
          <h2 className="font-display text-lg font-semibold text-ink mb-4">
            Open reports ({unresolvedReports.length})
          </h2>
          {unresolvedReports.length === 0 ? (
            <p className="text-sm text-gray-500 pb-6">No open reports.</p>
          ) : (
            <div className="space-y-3 mb-6">
              {unresolvedReports.map((report) => (
                <div key={report.id} className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-ink">
                      Reported {report.targetType} — {report.targetId}
                    </p>
                    {report.reason && <p className="text-sm text-gray-500 mt-1">"{report.reason}"</p>}
                    <p className="text-xs text-gray-400 mt-1">{formatDate(report.createdAt)}</p>
                  </div>
                  <GradientButton onClick={() => handleResolveReport(report.id)} size="sm" variant="outline">
                    Mark resolved
                  </GradientButton>
                </div>
              ))}
            </div>
          )}
          {resolvedReports.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-gray-400 mb-3">Resolved ({resolvedReports.length})</h3>
              <div className="space-y-2">
                {resolvedReports.map((report) => (
                  <div key={report.id} className="text-sm text-gray-400 pb-2 border-b border-gray-50 last:border-0">
                    {report.targetType} {report.targetId} — {formatDate(report.createdAt)}
                  </div>
                ))}
              </div>
            </>
          )}
        </GlassCard>
      ) : tab === 'posts' ? (
        <GlassCard hover={false}>
          <h2 className="font-display text-lg font-semibold text-ink mb-4">All posts ({posts.length})</h2>
          <div className="space-y-3">
            {posts.map((post) => (
              <div key={post.id} className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-ink">
                    {post.authorDisplayName} · <span className="text-gray-400">{post.area}</span>
                  </p>
                  {post.content && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{post.content}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(post.createdAt)}
                    {post.removedByAuthor && ' · removed by author (visible to you only until permanently deleted)'}
                    {post.hiddenByAdmin && ' · hidden'}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <GradientButton
                    onClick={() => handleTogglePostHidden(post)}
                    size="sm"
                    variant="outline"
                    icon={post.hiddenByAdmin ? Eye : EyeOff}
                    disabled={deletingPostId === post.id}
                  >
                    {post.hiddenByAdmin ? 'Unhide' : 'Hide'}
                  </GradientButton>
                  <GradientButton
                    onClick={() => handleDeletePost(post)}
                    size="sm"
                    variant="outline"
                    icon={Trash2}
                    disabled={deletingPostId === post.id}
                    className="!border-red-200 !text-red-600 hover:!bg-red-50 hover:!border-red-300"
                  >
                    {deletingPostId === post.id ? 'Deleting…' : 'Delete permanently'}
                  </GradientButton>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      ) : tab === 'members' ? (
        <GlassCard hover={false}>
          <h2 className="font-display text-lg font-semibold text-ink mb-4">Members ({members.length})</h2>
          <div className="space-y-3">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between gap-3 pb-3 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-ink">{member.displayName || 'Member'}</p>
                  <p className="text-xs text-gray-400">
                    Joined {member.guidelinesAcknowledgedAt ? formatDate(member.guidelinesAcknowledgedAt) : 'not yet'}
                    {member.suspended && ' · suspended'}
                  </p>
                </div>
                <GradientButton
                  onClick={() => handleToggleSuspend(member)}
                  size="sm"
                  variant="outline"
                  icon={member.suspended ? ShieldCheck : ShieldAlert}
                >
                  {member.suspended ? 'Unsuspend' : 'Suspend'}
                </GradientButton>
              </div>
            ))}
          </div>
        </GlassCard>
      ) : (
        <GlassCard hover={false}>
          <h2 className="font-display text-lg font-semibold text-ink mb-1">Introduction requests ({introductions.length})</h2>
          <p className="text-sm text-gray-500 mb-4">
            Every request a member sent — to another member, or to you directly. A request to a specific member
            needs that member's consent before you coordinate anything; a "help me find a connection" request is
            yours to review and match by hand, with both members' consent, before introducing anyone.
          </p>
          <div className="space-y-3">
            {introductions.length === 0 ? (
              <p className="text-sm text-gray-500">No introduction requests yet.</p>
            ) : (
              introductions.map((req) => {
                const toCassie = adminUserId != null && req.recipientId === adminUserId
                const isHelpFind = toCassie && req.note?.trim() === HELP_FIND_CONNECTION_NOTE
                const headline = isHelpFind
                  ? `${memberNameOf(req.requesterId)} asked you to help find a connection`
                  : toCassie
                  ? `${memberNameOf(req.requesterId)} requested to connect with you directly`
                  : `${memberNameOf(req.requesterId)} requested an introduction to ${memberNameOf(req.recipientId)}`
                return (
                  <div key={req.id} className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-ink">{headline}</p>
                      {req.note && !isHelpFind && <p className="text-sm text-gray-500 mt-1">"{req.note}"</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        {statusLabel[req.status] || req.status} · {formatDate(req.createdAt)}
                      </p>
                    </div>
                    {req.status !== 'closed' && (
                      <GradientButton
                        onClick={() => handleCloseIntroduction(req.id, 'handled')}
                        size="sm"
                        variant="outline"
                      >
                        Mark handled
                      </GradientButton>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </GlassCard>
      )}
    </PanelShell>
  )
}

export default AdminCommunity
